import { createContext, useContext, useState, useEffect, useCallback } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
    const [user, setUser] = useState(null);
    const [token, setToken] = useState(localStorage.getItem('token'));
    const [loading, setLoading] = useState(true);

    const logout = useCallback(() => {
        localStorage.removeItem('token');
        localStorage.removeItem('refresh_token');
        setToken(null);
        setUser(null);
    }, []);

    const refreshToken = useCallback(async () => {
        const storedRefresh = localStorage.getItem('refresh_token');
        if (!storedRefresh) return null;
        try {
            const response = await axios.post(`${API}/auth/refresh`, { refresh_token: storedRefresh });
            const { access_token, refresh_token: newRefresh } = response.data;
            localStorage.setItem('token', access_token);
            localStorage.setItem('refresh_token', newRefresh);
            setToken(access_token);
            return access_token;
        } catch {
            logout();
            return null;
        }
    }, [logout]);

    useEffect(() => {
        const fetchUser = async () => {
            if (token) {
                try {
                    const response = await axios.get(`${API}/auth/me`, {
                        headers: { Authorization: `Bearer ${token}` }
                    });
                    setUser(response.data);
                } catch (error) {
                    if (error.response?.status === 401) {
                        const newToken = await refreshToken();
                        if (newToken) {
                            try {
                                const retry = await axios.get(`${API}/auth/me`, {
                                    headers: { Authorization: `Bearer ${newToken}` }
                                });
                                setUser(retry.data);
                            } catch {
                                logout();
                            }
                        }
                    } else {
                        logout();
                    }
                }
            }
            setLoading(false);
        };
        fetchUser();
    }, [token, logout, refreshToken]);

    const login = async (email, password) => {
        const response = await axios.post(`${API}/auth/login`, { email, password });
        const { access_token, refresh_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const register = async (name, email, password, role = 'cashier') => {
        const response = await axios.post(`${API}/auth/register`, { name, email, password, role });
        const { access_token, refresh_token, user: userData } = response.data;
        localStorage.setItem('token', access_token);
        if (refresh_token) localStorage.setItem('refresh_token', refresh_token);
        setToken(access_token);
        setUser(userData);
        return userData;
    };

    const loginAs = useCallback((tokenData) => {
        const prevToken = localStorage.getItem('token');
        const prevUser = localStorage.getItem('user_name_backup');
        localStorage.setItem('admin_token_backup', prevToken || '');
        localStorage.setItem('token', tokenData.access_token);
        localStorage.removeItem('refresh_token');
        setToken(tokenData.access_token);
        setUser(tokenData.user);
    }, []);

    const restoreAdmin = useCallback(() => {
        const backup = localStorage.getItem('admin_token_backup');
        if (backup) {
            localStorage.setItem('token', backup);
            localStorage.removeItem('admin_token_backup');
            setToken(backup);
            setUser(null);
            window.location.href = '/admin';
        } else {
            logout();
        }
    }, [logout]);

    const refreshUser = useCallback(async () => {
        const storedToken = localStorage.getItem('token');
        if (!storedToken) return;
        try {
            const response = await axios.get(`${API}/auth/me`, {
                headers: { Authorization: `Bearer ${storedToken}` }
            });
            setUser(response.data);
        } catch { logout(); }
    }, [logout]);

    const value = {
        user,
        token,
        loading,
        login,
        loginAs,
        restoreAdmin,
        register,
        logout,
        refreshToken,
        refreshUser,
        isAuthenticated: !!user,
        isAdmin: user?.role === 'admin',
        isManager: user?.role === 'manager' || user?.role === 'admin',
        isCashier: user?.role === 'cashier',
        isImpersonating: !!localStorage.getItem('admin_token_backup'),
    };

    return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error('useAuth must be used within an AuthProvider');
    }
    return context;
}
